import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../services/api';
import SecureLS from 'secure-ls';
import { setUserFirstName } from '../utils/userUtils';
import PasswordStrengthMeter from '../components/auth/PasswordStrengthMeter';
import { setUser, setFamilies } from '../utils/secureStorage';

const ls = new SecureLS({ encodingType: 'aes' });

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState(false);

  // Validate token on mount and get the associated email
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axiosInstance.get(`/account/auth/reset-password/?token=${token}`);
        setEmail(response.data.email);
      } catch (err) {
        setTokenError(true);
        setError(err.response?.data?.error || 'This reset link is no longer valid.');
      } finally {
        setValidating(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/account/auth/reset-password/', {
        token,
        password,
        confirm_password: confirmPassword
      });

      const userData = response.data;

      // Auto-login: store data securely (encrypted)
      setUser({
        id: userData.user.id,
        person_id: userData.user.person_id,
        email: userData.user.email,
        phone: userData.user.phone,
        first_name: userData.user.first_name,
        last_name: userData.user.last_name,
        age: userData.user.age
      });

      if (userData.families && userData.families.length > 0) {
        setFamilies(userData.families);
        if (!localStorage.getItem('familyId')) {
          localStorage.setItem('familyId', userData.families[0].id);
        }
      }

      setUserFirstName(userData.user.first_name);
      ls.set('authToken', userData.token);
      localStorage.setItem('isLoggedIn', 'true');

      navigate('/my-life');
    } catch (err) {
      const data = err.response?.data;
      if (data?.error?.includes('expired') || data?.error?.includes('Invalid')) {
        setTokenError(true);
        setError(data.error);
      } else {
        setError(data?.error || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const EyeIcon = ({ show }) => show ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

  if (validating) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center">
        <p className="text-lg text-gray-600">Validating your reset link...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-300">
          {/* Header */}
          <div className="bg-[#556B2F] text-white p-6 text-center">
            <h1 className="text-2xl font-bold mb-2">Set New Password</h1>
            <p className="text-base opacity-90">Choose a strong password for your account</p>
          </div>

          {/* Content */}
          <div className="p-8">
            {tokenError ? (
              /* Token Error State */
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Link Expired or Invalid</h2>
                <p className="text-gray-600 text-base">
                  {error || 'This password reset link is no longer valid.'}
                </p>
                <p className="text-sm text-gray-500">
                  Password reset links expire after 24 hours and can only be used once.
                </p>
                <div className="pt-4 space-y-2">
                  <Link
                    to="/forgot-password"
                    className="block w-full bg-[#556B2F] text-white border-none py-3 px-4 text-lg font-bold rounded-lg text-center transition-colors hover:bg-[#4A5D23]"
                  >
                    Request a New Link
                  </Link>
                  <Link
                    to="/login"
                    className="block text-[#556B2F] font-medium hover:underline text-base text-center pt-2"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            ) : (
              /* Form State */
              <>
                {email && (
                  <p className="text-sm text-gray-500 text-center mt-2 mb-2">
                    Resetting password for <strong>{email}</strong>
                  </p>
                )}
                <form className="mt-4 space-y-6" onSubmit={handleSubmit}>
                  {/* Hidden email field for browser password manager */}
                  <input
                    type="email"
                    name="email"
                    autoComplete="username"
                    value={email}
                    readOnly
                    hidden
                  />
                  {error && (
                    <div className="text-red-500 text-sm text-center">{error}</div>
                  )}
                  <div>
                    <label htmlFor="password" className="block text-base font-bold text-gray-700 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none pr-12"
                        placeholder="At least 8 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <EyeIcon show={showPassword} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="confirm-password" className="block text-base font-bold text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirm-password"
                        name="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none pr-12"
                        placeholder="Re-enter your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        <EyeIcon show={showConfirmPassword} />
                      </button>
                    </div>
                  </div>
                  <PasswordStrengthMeter password={password} confirmPassword={confirmPassword} />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">Passwords don't match</p>
                  )}
                  <div>
                    <button
                      type="submit"
                      disabled={loading || password.length < 8 || password !== confirmPassword || !(/[0-9]/.test(password) || /[^a-zA-Z0-9]/.test(password))}
                      className="w-full bg-[#556B2F] text-white border-none py-4 px-4 text-lg font-bold rounded-lg cursor-pointer transition-colors hover:bg-[#4A5D23] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Resetting...' : 'Set New Password'}
                    </button>
                  </div>
                </form>
                <div className="text-sm text-center mt-4">
                  <Link to="/login" className="font-medium text-[#556B2F] hover:underline">
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
